import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import CustomPackageCard from '@/components/CustomPackageCard';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSupabaseClient } from '@/lib/env';
import type { CustomPackage } from '@/types/packages';

export default function FeaturedCustomPackages() {
  const [packages, setPackages] = useState<CustomPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    async function loadCustomPackages() {
      try {
        setLoading(true);
        setError(null);
        
        const supabase = await getSupabaseClient();
        
        // Fetch custom packages with their features
        const { data, error } = await supabase
          .from('custom_packages')
          .select('*, features:package_features(*), seller_profile:seller_profiles(*)')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (error) throw error;
        
        if (isMounted) {
          setPackages(data as CustomPackage[]);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error in FeaturedCustomPackages component:', err);
        if (isMounted) {
          setError('Failed to load custom packages');
          setLoading(false);
        }
      }
    }

    loadCustomPackages();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="py-8 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 flex justify-center">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 text-center">
            <p className="text-red-500">{error}</p>
            <Button 
              variant="outline" 
              className="mt-4" 
              onClick={() => {
                setLoading(true);
                getSupabaseClient()
                  .then(client => client
                    .from('custom_packages')
                    .select('*, features:package_features(*), seller_profile:seller_profiles(*)')
                    .eq('is_active', true)
                    .order('created_at', { ascending: false })
                    .limit(5)
                  )
                  .then(({ data }) => {
                    if (data) {
                      setPackages(data as CustomPackage[]);
                      setError(null);
                    }
                    setLoading(false);
                  })
                  .catch(() => {
                    setError('Failed to reload custom packages');
                    setLoading(false);
                  });
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="py-8 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No custom packages available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-8 md:py-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h2 className="text-2xl font-bold">Premium Custom Packages</h2>
          <Link to="/custom-packages">
            <Button variant="outline" className="w-full sm:w-auto flex items-center gap-2">
              View All Packages
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 overflow-hidden">
          {packages.map((pkg) => (
            <CustomPackageCard 
              key={pkg.id}
              package={pkg}
            />
          ))}
        </div>
      </div>
    </section>
  );
} 